import React from 'react';
import { Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Shield, 
  CheckCircle, 
  Eye, 
  MessageSquare, 
  Settings, 
  Heart, 
  Baby, 
  ArrowRight,
  Lock,
  AlertCircle,
  Users,
  Clock
} from 'lucide-react';

const ParentSection: React.FC = () => {
  return (
    <section className="py-16 md:py-24 bg-gray-900">
      <div className="container mx-auto px-6">
        <Tabs defaultValue="safety" className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 mb-12 max-w-4xl mx-auto">
            <TabsTrigger value="safety">Safety First</TabsTrigger>
            <TabsTrigger value="info">Information</TabsTrigger>
            <TabsTrigger value="mission">Our Mission</TabsTrigger>
            <TabsTrigger value="signup">Parent Sign Up</TabsTrigger>
          </TabsList>

          {/* Tab 1: Safety First */}
          <TabsContent value="safety" className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                Youth Safety is Our <span className="text-sports-green">Top Priority</span>
              </h2>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                We've created a safe, supervised environment where young athletes can train, connect, 
                and grow under the watchful eye of parents, coaches, and safety moderators.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-white text-center mb-8">Safety Measures</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <Shield className="h-10 w-10 text-sports-green mb-4" />
                    <h4 className="font-semibold text-lg mb-2 text-white">Age-Appropriate Content</h4>
                    <p className="text-sm text-gray-300">
                      Smart filtering ensures all content is suitable for young athletes
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <Users className="h-10 w-10 text-sports-green mb-4" />
                    <h4 className="font-semibold text-lg mb-2 text-white">Parental Oversight</h4>
                    <p className="text-sm text-gray-300">
                      Complete control and approval system for all child activities
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <Lock className="h-10 w-10 text-sports-green mb-4" />
                    <h4 className="font-semibold text-lg mb-2 text-white">Secure Communications</h4>
                    <p className="text-sm text-gray-300">
                      Encrypted channels with monitored messaging for youth safety
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <CheckCircle className="h-10 w-10 text-sports-green mb-4" />
                    <h4 className="font-semibold text-lg mb-2 text-white">Coach Verification</h4>
                    <p className="text-sm text-gray-300">
                      All coaches undergo professional background checks and verification
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <Clock className="h-10 w-10 text-sports-green mb-4" />
                    <h4 className="font-semibold text-lg mb-2 text-white">24/7 Safety Monitoring</h4>
                    <p className="text-sm text-gray-300">
                      Round-the-clock automated and human moderation systems
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <AlertCircle className="h-10 w-10 text-sports-green mb-4" />
                    <h4 className="font-semibold text-lg mb-2 text-white">Incident Reporting</h4>
                    <p className="text-sm text-gray-300">
                      Quick and easy reporting system with immediate response protocols
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Tab 2: Information */}
          <TabsContent value="info" className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                <span className="text-sports-green">Parent</span> Dashboard Features
              </h2>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                Stay informed and involved in your child's sports journey with comprehensive tools
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <Shield className="h-12 w-12 text-sports-green mb-4" />
                  <h4 className="font-semibold text-xl mb-4 text-white">Complete Safety Control</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>Approve or deny team join requests</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>Control communication permissions</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>Manage privacy settings and visibility</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>Set content and interaction boundaries</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <Eye className="h-12 w-12 text-sports-green mb-4" />
                  <h4 className="font-semibold text-xl mb-4 text-white">Monitor Activity</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>View all child account activities</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>Track team participation and schedules</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>Review messages and interactions</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>Receive instant activity notifications</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <MessageSquare className="h-12 w-12 text-sports-green mb-4" />
                  <h4 className="font-semibold text-xl mb-4 text-white">Direct Communication</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>Message coaches and team administrators</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>Connect with other parents securely</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>Receive important team announcements</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>Join parent community groups</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <Settings className="h-12 w-12 text-sports-green mb-4" />
                  <h4 className="font-semibold text-xl mb-4 text-white">Settings & Privacy</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>Manage multiple child accounts</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>Customize notification preferences</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>Control data sharing and visibility</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-sports-green mr-2 mt-0.5 flex-shrink-0" />
                      <span>Access family schedule calendar</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tab 3: Our Mission */}
          <TabsContent value="mission" className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                Our <span className="text-sports-green">Mission</span> & Commitment
              </h2>
            </div>

            <div className="max-w-4xl mx-auto">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-12 text-center">
                  <Heart className="h-20 w-20 text-sports-green mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-white mb-6">
                    Creating Safe Sports Communities
                  </h3>
                  <div className="space-y-4 text-gray-300 text-lg">
                    <p>
                      At Players Lockeroom, we believe every young athlete deserves a safe, supportive 
                      environment to pursue their passion for sports. Our mission is to provide parents 
                      with the tools and peace of mind they need to confidently support their children's 
                      athletic journey.
                    </p>
                    <p>
                      We're committed to maintaining the highest safety standards, fostering positive 
                      community connections, and empowering parents to stay involved every step of the way. 
                      Your child's safety and development are our top priorities, and we continuously work 
                      to ensure our platform provides a secure, enriching experience for young athletes.
                    </p>
                  </div>
                  <div className="mt-8 pt-8 border-t border-gray-700">
                    <p className="text-sports-green font-semibold text-xl">
                      Safety. Community. Empowerment. Join thousands of families who trust us.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tab 4: Parent Sign Up */}
          <TabsContent value="signup" className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                Create Your <span className="text-sports-green">Parent Account</span>
              </h2>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                Join thousands of parents who trust us to provide a safe platform for their young athletes
              </p>
            </div>

            <div className="max-w-2xl mx-auto">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-12 text-center">
                  <Baby className="h-24 w-24 text-sports-green mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Ready to Get Started?
                  </h3>
                  <p className="text-gray-300 text-lg mb-8">
                    Create your parent account today and gain full control over your child's 
                    sports experience. It only takes a few minutes to set up and start managing 
                    your family's sports activities safely.
                  </p>
                  <Button 
                    asChild 
                    className="bg-sports-green hover:bg-sports-green/90 text-black font-bold px-8 py-6 h-auto text-lg rounded-md"
                  >
                    <Link to="/register?role=parent" className="flex items-center">
                      Create Parent Account Now
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <p className="text-sm text-gray-400 mt-6">
                    By creating an account, you agree to our Terms of Service and Privacy Policy
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default ParentSection;
